import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { type AdminOrg, listOrgs, patchOrg } from '@/features/admin/admin-api';
import { useFormatters } from '@/i18n/format';
import { ApiError } from '@/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Building2, Search } from 'lucide-react';
import { type FormEvent, type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const Route = createFileRoute('/admin/orgs')({
  component: AdminOrgsPage,
});

function orgInitials(value: string): string {
  const parts = value.trim().split(/\s+/u).filter(Boolean);
  const first = parts[0] ?? '';
  if (parts.length === 0) return '?';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const second = parts[1] ?? '';
  return ((first[0] ?? '') + (second[0] ?? '')).toUpperCase();
}

function AdminOrgsPage(): ReactElement {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const orgs = useQuery({
    queryKey: ['admin', 'orgs', search],
    queryFn: () => listOrgs({ search: search || undefined, size: 50 }),
  });

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('admin.nav.adminPanel', { defaultValue: 'Admin' })}
        title={t('admin.orgs.title')}
        description={t('admin.orgs.description')}
      />

      <div className="relative max-w-sm">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          placeholder={t('admin.orgs.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-10 pl-9"
        />
      </div>

      {orgs.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
        </div>
      ) : (orgs.data?.orgs ?? []).length === 0 ? (
        <EmptyState icon={Building2} title={t('admin.orgs.empty')} />
      ) : (
        <ul className="space-y-2">
          {orgs.data?.orgs.map((o) => (
            <li key={o.id}>
              <OrgRow org={o} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OrgRow({ org }: { org: AdminOrg }): ReactElement {
  const { t } = useTranslation();
  const { date } = useFormatters();
  const queryClient = useQueryClient();
  const [renameOpen, setRenameOpen] = useState(false);
  const disable = useMutation({
    mutationFn: () => patchOrg(org.id, { disabled: !org.disabledAt }),
    onSuccess: () => {
      toast.success(org.disabledAt ? t('admin.orgs.enabledToast') : t('admin.orgs.disabledToast'));
      queryClient.invalidateQueries({ queryKey: ['admin', 'orgs'] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : t('admin.orgs.actionFailed')),
  });
  const plan = useMutation({
    mutationFn: () => patchOrg(org.id, { plan: org.plan === 'pro' ? 'free' : 'pro' }),
    onSuccess: () => {
      toast.success(t('admin.orgs.planUpdatedToast'));
      queryClient.invalidateQueries({ queryKey: ['admin', 'orgs'] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : t('admin.orgs.actionFailed')),
  });

  return (
    <Card className="border-border/70 shadow-soft">
      <CardHeader className="flex flex-row items-start gap-3 space-y-0">
        <Avatar className="h-10 w-10">
          <AvatarFallback>{orgInitials(org.name)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 space-y-1">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            <span>{org.name}</span>
            <Badge variant="outline" className="font-mono text-[10px]">
              {org.slug}
            </Badge>
            <Badge variant={org.plan === 'pro' ? 'default' : 'secondary'} className="uppercase">
              {org.plan}
            </Badge>
            {org.disabledAt ? (
              <Badge variant="destructive">{t('admin.orgs.disabledSuffix')}</Badge>
            ) : null}
          </CardTitle>
          <CardDescription>
            <code className="font-mono text-xs">{org.id}</code> ·{' '}
            {t('admin.orgs.members', { count: org.memberCount })} ·{' '}
            {t('admin.orgs.created', { when: date(org.createdAt) })}
          </CardDescription>
        </div>
        <div className="flex flex-col gap-1.5">
          <Button variant="outline" size="sm" onClick={() => setRenameOpen(true)}>
            {t('admin.orgs.rename')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => disable.mutate()}
            disabled={disable.isPending}
          >
            {org.disabledAt ? t('admin.orgs.reactivate') : t('admin.orgs.deactivate')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => plan.mutate()}
            disabled={plan.isPending}
          >
            {t('admin.orgs.planToggle', { target: org.plan === 'pro' ? 'free' : 'pro' })}
          </Button>
        </div>
      </CardHeader>
      <RenameOrgDialog
        org={org}
        open={renameOpen}
        onOpenChange={setRenameOpen}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['admin', 'orgs'] });
          setRenameOpen(false);
        }}
      />
    </Card>
  );
}

function RenameOrgDialog({
  org,
  open,
  onOpenChange,
  onSuccess,
}: {
  org: AdminOrg;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onSuccess: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const [value, setValue] = useState(org.name);
  const rename = useMutation({
    mutationFn: (name: string) => patchOrg(org.id, { name }),
    onSuccess: () => {
      toast.success(t('admin.orgs.renamedToast'));
      onSuccess();
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : t('admin.orgs.actionFailed')),
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed || trimmed === org.name) return;
    rename.mutate(trimmed);
  }

  function handleOpenChange(next: boolean): void {
    if (!next) setValue(org.name);
    onOpenChange(next);
  }

  const trimmed = value.trim();
  const canSave = trimmed.length > 0 && trimmed.length <= 120 && trimmed !== org.name;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{t('admin.orgs.renameTitle')}</DialogTitle>
            <DialogDescription>{t('admin.orgs.renameDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label htmlFor={`rename-${org.id}`} className="text-sm font-medium">
              {t('admin.orgs.nameLabel')}
            </label>
            <Input
              id={`rename-${org.id}`}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              maxLength={120}
              autoFocus
              disabled={rename.isPending}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={rename.isPending}
            >
              {t('admin.orgs.cancel')}
            </Button>
            <Button type="submit" disabled={!canSave || rename.isPending}>
              {t('admin.orgs.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
